import {
    Dialog,
    DialogContent,
    FormGroup,
    FormHelperText,
    IconButton,
    Switch,
    Typography,
} from '@mui/material';
import {
    CheckCircleOutline,
    Close,
    ErrorOutline,
    InfoOutlined,
    KeyboardArrowDown,
} from '@mui/icons-material';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { LoggedInUser } from '../../../types/auth.types';
import { EXPRESS_VALIDITY_TEXT } from '../../../common/expressConsent';
import { getLoggedUser } from '../../api/auth.api';
import { uploadUserCV } from '../../api/user.api';
import Button from '../foundation/Button';
import CvUploadDropZone from '../components/CvUploadDropZone';
import GoogleLogin from '../components/GoogleLogin';
import UserNotification, { UserNotificationProps } from '../components/UserNotification';
import Page from '../layout/base.page';
import RegisterModal from '../modals/RegisterModal/RegisterModal';
import {
    ExpressActions,
    ExpressConsentDivider,
    ExpressConsentGrid,
    ExpressConsentLabel,
    ExpressConsentToggleRow,
    ExpressConsentText,
    ExpressRequiredMark,
    ExpressDropZoneFrame,
    ExpressGoogleAction,
    ExpressUploadArea,
    FeedbackDialogContent,
    FeedbackDialogIcon,
    LoggedInUploadAction,
    FormSection,
    HeroBrand,
    HeroContainer,
    HeroContent,
    HeroCtaButton,
    HeroDot,
    HeroImage,
    HeroNote,
    HeroNotes,
    HeroSection,
    HeroSubtitle,
    HeroTitle,
    HeroTitleAccent,
    HeroVisual,
    MainPanel,
    PageContainer,
    PageShell,
    SelectedFileText,
    SuccessActions,
    SuccessContent,
    SuccessIcon,
} from './ResumeUploadLanding.page.styles';
import CustomBR from '../foundation/CustomBR';
import RegistrationTermsText from '../components/RegistrationTermsText/RegistrationTermsText';
import WhatsAppEntryGate from '../components/WhatsAppEntryGate/WhatsAppEntryGate';

export type ResumeUploadLandingPageProps = {
    url: string
    user?: LoggedInUser
}

type FlowStage = 'idle' | 'registering' | 'registered' | 'uploadingCv' | 'success';
type FeedbackDialogSeverity = 'error' | 'info';
type FeedbackDialogState = {
    title: string
    message: string
    severity: FeedbackDialogSeverity
};

const getErrorMessage = (error: unknown, fallback: string): string => (
    error instanceof Error && error.message ? error.message : fallback
);

const heroContainerStyle: CSSProperties = {
    direction: 'ltr',
    gridTemplateAreas: '"visual content"',
};

const heroContentStyle: CSSProperties = {
    direction: 'rtl',
    textAlign: 'right',
};

const heroTextStyle: CSSProperties = {
    direction: 'rtl',
    textAlign: 'right',
};

const heroBrandStyle: CSSProperties = {
    direction: 'ltr',
    textAlign: 'right',
    width: '100%',
};

const feedbackDialogPaperStyle: CSSProperties = {
    borderRadius: 12,
    direction: 'rtl',
    overflow: 'visible',
};

const feedbackDialogCloseButtonStyle: CSSProperties = {
    position: 'absolute',
    right: '1rem',
    top: '1rem',
};

const feedbackDialogContentStyle: CSSProperties = {
    direction: 'rtl',
    padding: '3rem 3.5rem 2.75rem',
    position: 'relative',
    textAlign: 'center',
};

function ResumeUploadLandingPage(props: ResumeUploadLandingPageProps): JSX.Element {
    const formSectionRef = useRef<HTMLElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [registeredUser, setRegisteredUser] = useState<LoggedInUser | null>(props.user || null);
    const [stage, setStage] = useState<FlowStage>(props.user ? 'registered' : 'idle');
    const [showEmailRegisterModal, setShowEmailRegisterModal] = useState(false);
    const [feedbackDialog, setFeedbackDialog] = useState<FeedbackDialogState | null>(null);
    const [cvNotification, setCvNotification] = useState<UserNotificationProps>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [showToEmployers, setShowToEmployers] = useState(true);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [fieldErrorKey, setFieldErrorKey] = useState(0);
    const isBusy = stage === 'registering' || stage === 'uploadingCv';

    const handleWhatsAppUserChange = useCallback((entryUser?: LoggedInUser) => {
        setRegisteredUser(entryUser || null);
        setStage(entryUser ? 'registered' : 'idle');
    }, []);

    const closeFeedbackDialog = () => {
        setFeedbackDialog(null);
    };

    const showFeedbackDialog = (dialog: FeedbackDialogState) => {
        setFeedbackDialog(dialog);
    };

    const notifyCvUpload = (message: string, severity: UserNotificationProps['severity']) => {
        setCvNotification({ id: Math.random(), message, severity });
    };

    const handleFileSelected = (file: File) => {
        setSelectedFile(file);
        setFileError(null);
        setCvNotification(null);
        if (stage === 'success') setStage(registeredUser ? 'registered' : 'idle');
    };

    const setMissingFileError = () => {
        setFileError('יש להעלות קובץ קורות חיים לפני ההרשמה.');
    };

    const flashFieldErrors = (showFileError: boolean, showTermsError: boolean) => {
        setFieldErrorKey(prev => prev + 1);
        if (showFileError) setFileError(null);
        if (showTermsError) setTermsError(false);
        window.setTimeout(() => {
            if (showFileError) setMissingFileError();
            if (showTermsError) setTermsError(true);
        }, 60);
    };

    const handleFileValidationError = (message: string) => {
        if (registeredUser) {
            notifyCvUpload(message, 'error');
            return;
        }
        setFileError(message);
    };

    const uploadCvForUser = async (user: LoggedInUser, file: File) => {
        setStage('uploadingCv');
        try {
            const formData = new FormData();
            formData.append('file', file);
            await uploadUserCV({ userid: user.userid }, formData);
            setStage('success');
            setCvNotification(null);
        } catch (error) {
            Sentry.captureException(error);
            setStage('registered');
            notifyCvUpload(getErrorMessage(error, 'אירעה שגיאה בהעלאת הקובץ'), 'error');
        }
    };

    const validateExpressStart = () => {
        const showFileError = !selectedFile;
        const showTermsError = !termsAccepted;
        if (showFileError || showTermsError) {
            flashFieldErrors(showFileError, showTermsError);
            return false;
        }
        return true;
    };

    const openEmailRegisterModal = () => {
        if (isBusy) return;
        if (validateExpressStart()) {
            setShowEmailRegisterModal(true);
        }
    };

    const handleEmailRegistrationComplete = async (user: LoggedInUser) => {
        setShowEmailRegisterModal(false);
        setRegisteredUser(user);
        setStage('registered');
        if (!selectedFile) {
            setMissingFileError();
            return;
        }
        await uploadCvForUser(user, selectedFile);
    };

    const handleGoogleSuccess = async () => {
        if (!selectedFile) {
            setMissingFileError();
            return;
        }

        setStage('registering');

        try {
            const userResponse = await getLoggedUser();
            setRegisteredUser(userResponse.data);
            setStage('registered');
            await uploadCvForUser(userResponse.data, selectedFile);
        } catch (error) {
            Sentry.captureException(error);
            setStage('idle');
            showFeedbackDialog({
                severity: 'error',
                title: 'ההרשמה עם Google נכשלה',
                message: getErrorMessage(error, 'ההרשמה עם Google לא הושלמה. נא לנסות שוב.'),
            });
        }
    };

    const retryCvUpload = async () => {
        if (!registeredUser || !selectedFile || isBusy) return;
        await uploadCvForUser(registeredUser, selectedFile);
    };

    const scrollToUploadFlow = () => {
        const formTop = formSectionRef.current?.getBoundingClientRect().top;
        if (formTop === undefined) return;
        window.scrollTo({
            behavior: 'smooth',
            top: window.scrollY + formTop - 260,
        });
    };

    const renderFeedbackIcon = () => {
        if (feedbackDialog?.severity === 'error') return <ErrorOutline fontSize="large" />;
        return <InfoOutlined fontSize="large" />;
    };

    return (
        <Page
            url={props.url}
            user={registeredUser || props.user}
            showBreadCrumbs={false}
            showNavbarExpressBadge
            showPageHeader={false}
            pageHeaderTitle="Mploy - העלאת קורות חיים"
        >
            <PageShell>
                <WhatsAppEntryGate
                    user={registeredUser || undefined}
                    onUserChange={handleWhatsAppUserChange}
                />
                <HeroSection>
                    <HeroContainer maxWidth="lg" style={heroContainerStyle}>
                        <HeroVisual>
                            <HeroImage
                                alt="Mploy Express"
                                src="/images/resume-upload/man.png"
                            />
                        </HeroVisual>
                        <CustomBR h={22} />
                        <HeroContent dir="rtl" style={heroContentStyle}>
                            <HeroTitle component="h1" dir="rtl" style={heroTextStyle}>
                                תנו לקו"ח שלכם
                                <HeroTitleAccent>לעבוד בשבילכם</HeroTitleAccent>
                            </HeroTitle>
                            <HeroBrand dir="ltr" style={heroBrandStyle}>Mploy Express</HeroBrand>
                            <HeroSubtitle dir="rtl" style={heroTextStyle}>
                                מחפש את ג׳וב החלומות שלך? מעלים קו"ח ואנחנו ממליצים עליכם למעסיקים שמחפשים בדיוק אתכם.
                            </HeroSubtitle>
                            <HeroCtaButton
                                color="secondary"
                                endIcon={<KeyboardArrowDown />}
                                onClick={scrollToUploadFlow}
                                size="medium"
                                type="button"
                            >
                                העלאת קו"ח
                            </HeroCtaButton>
                            <HeroNotes>
                                <HeroNote>תהליך קצר</HeroNote>
                                <HeroDot />
                                <HeroNote>ללא התחייבות</HeroNote>
                                <HeroDot />
                                <HeroNote>אפשר לעדכן או להסיר בכל שלב</HeroNote>
                            </HeroNotes>
                        </HeroContent>
                    </HeroContainer>
                </HeroSection>

                <FormSection ref={formSectionRef}>
                    <PageContainer maxWidth="lg">
                        <MainPanel $centerContent={!!registeredUser && stage !== 'success'} dir="rtl">
                            {stage === 'success'
                                ? <SuccessContent>
                                    <SuccessIcon>
                                        <CheckCircleOutline />
                                    </SuccessIcon>
                                    <Typography color="secondary" fontWeight="bold" variant="h5">
                                        ההרשמה והעלאת קורות החיים הושלמו בהצלחה
                                    </Typography>
                                    <Typography color="text.secondary" variant="body1">
                                        עכשיו אפשר לנהל את הפרטים שלכם או להתחיל לחפש משרות שמתאימות לכם
                                    </Typography>
                                    <SuccessActions>
                                        <Button
                                            color="primary"
                                            fullWidth
                                            href="/user/cvs"
                                            size="medium"
                                            variant="contained"
                                        >
                                            לניהול הפרטים וקורות החיים
                                        </Button>
                                        <Button
                                            color="primary"
                                            fullWidth
                                            href="/"
                                            size="medium"
                                            variant="outlined"
                                        >
                                            לחיפוש משרות
                                        </Button>
                                    </SuccessActions>
                                </SuccessContent>
                                : <>
                                    <ExpressUploadArea $lifted={!!registeredUser && stage !== 'success'}>
                                        <Typography color="secondary" fontWeight="bold" variant="subtitle1">
                                    העלאת קורות חיים
                                        </Typography>
                                        {registeredUser && cvNotification && <UserNotification blink disableScroll {...cvNotification} />}
                                        <ExpressDropZoneFrame>
                                            <CvUploadDropZone
                                                disabled={isBusy}
                                                isUploading={stage === 'uploadingCv'}
                                                mobileEmptyLabel="לחץ כאן לבחירת קובץ"
                                                selectedFile={selectedFile}
                                                onFileSelected={handleFileSelected}
                                                onValidationError={handleFileValidationError}
                                            />
                                        </ExpressDropZoneFrame>
                                        {fileError && <FormHelperText key={`file-error-${fieldErrorKey}`} error>{fileError}</FormHelperText>}
                                        {selectedFile && <SelectedFileText color="text.secondary" variant="body2">
                                    הקובץ שנבחר: {selectedFile.name}
                                        </SelectedFileText>}
                                    </ExpressUploadArea>

                                    {registeredUser
                                        ? <>
                                            {stage !== 'success' && <LoggedInUploadAction>
                                                <Button
                                                    color="primary"
                                                    disabled={!selectedFile || isBusy}
                                                    fullWidth
                                                    onClick={retryCvUpload}
                                                    size="medium"
                                                >
                                        העלאת קורות חיים
                                                </Button>
                                            </LoggedInUploadAction>}
                                        </>
                                        : <>
                                            <ExpressConsentGrid>
                                                <FormGroup>
                                                    <ExpressConsentToggleRow>
                                                        <Switch
                                                            checked={showToEmployers}
                                                            inputProps={{ 'aria-label': 'אישור שליחת קורות חיים למעסיקים' }}
                                                            name="showToEmployers"
                                                            onChange={ev => setShowToEmployers(ev.target.checked)}
                                                        />
                                                        <ExpressConsentLabel>אישור שליחת קורות חיים למעסיקים</ExpressConsentLabel>
                                                    </ExpressConsentToggleRow>
                                                    <ExpressConsentText>
                                                הפעלת השירות מהווה אישור ל־Mploy לשתף את קורות החיים שלך עם מעסיקים
                                                        <br />
                                                        {EXPRESS_VALIDITY_TEXT}
                                                    </ExpressConsentText>
                                                </FormGroup>

                                                <ExpressConsentDivider />

                                                <FormGroup>
                                                    <ExpressConsentToggleRow>
                                                        <Switch
                                                            checked={termsAccepted}
                                                            inputProps={{ 'aria-label': 'אישור התקנון ותנאי השימוש' }}
                                                            name="termsAccepted"
                                                            onChange={ev => {
                                                                setTermsAccepted(ev.target.checked);
                                                                if (ev.target.checked) setTermsError(false);
                                                            }}
                                                        />
                                                        <ExpressConsentLabel>
                                                    אישור התקנון ותנאי השימוש
                                                            <ExpressRequiredMark aria-hidden="true">*</ExpressRequiredMark>
                                                        </ExpressConsentLabel>
                                                    </ExpressConsentToggleRow>
                                                    <ExpressConsentText>
                                                        <RegistrationTermsText />
                                                    </ExpressConsentText>
                                                    {termsError && <FormHelperText key={`terms-error-${fieldErrorKey}`} error>
                                                יש לאשר את התקנון ותנאי השימוש כדי להמשיך בהרשמה.
                                                    </FormHelperText>}
                                                </FormGroup>
                                            </ExpressConsentGrid>

                                            <ExpressActions>
                                                <Button
                                                    color="primary"
                                                    disabled={isBusy}
                                                    fullWidth
                                                    onClick={openEmailRegisterModal}
                                                    size="medium"
                                                    type="button"
                                                >
                                            הרשמה עם שם משתמש וסיסמה
                                                </Button>
                                                <ExpressGoogleAction>
                                                    <GoogleLogin
                                                        flow="register"
                                                        appearance="outline"
                                                        buttonText="הרשמה עם גוגל"
                                                        disabled={isBusy}
                                                        showGoogleIcon
                                                        isAllowedToOpenPopup={validateExpressStart}
                                                        onAuthSuccess={handleGoogleSuccess}
                                                        showToEmployers={showToEmployers}
                                                    />
                                                </ExpressGoogleAction>
                                            </ExpressActions>
                                        </>}
                                </>}
                        </MainPanel>
                    </PageContainer>
                </FormSection>

                <RegisterModal
                    open={showEmailRegisterModal}
                    onClose={() => setShowEmailRegisterModal(false)}
                    gotoLogin={() => setShowEmailRegisterModal(false)}
                    onRegistrationComplete={handleEmailRegistrationComplete}
                    initialUseEmailPassword
                    showPhone={false}
                    showSmartAgent={false}
                    showToEmployers={showToEmployers}
                />

                <Dialog
                    open={!!feedbackDialog}
                    onClose={closeFeedbackDialog}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ style: feedbackDialogPaperStyle }}
                    aria-labelledby="express-feedback-dialog-title"
                >
                    <DialogContent style={feedbackDialogContentStyle}>
                        <IconButton
                            aria-label="סגירה"
                            onClick={closeFeedbackDialog}
                            style={feedbackDialogCloseButtonStyle}
                        >
                            <Close />
                        </IconButton>
                        <FeedbackDialogContent $severity={feedbackDialog?.severity || 'info'}>
                            <FeedbackDialogIcon $severity={feedbackDialog?.severity || 'info'}>
                                {renderFeedbackIcon()}
                            </FeedbackDialogIcon>
                            <Typography
                                id="express-feedback-dialog-title"
                                color="secondary"
                                fontWeight="bold"
                                variant="h4"
                            >
                                {feedbackDialog?.title}
                            </Typography>
                            <Typography color="text.secondary" variant="body1">{feedbackDialog?.message}</Typography>
                        </FeedbackDialogContent>
                    </DialogContent>
                </Dialog>
            </PageShell>
        </Page>
    );
}

export default ResumeUploadLandingPage;
